import useDebounce from 'Hooks/useDebounce';
import { AbortSignals } from 'Hooks/useAbortSignals';
import useQueryParams from 'Hooks/useQueryParams';
import { toInteger } from 'lodash';
import React, {
	createContext, useContext, useCallback, useMemo, useState, useEffect, Dispatch, SetStateAction,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { SAVE_STATUS } from 'Views/Components/AutoSaveHeader/AutoSaveHeader';
import { IStepLabel } from 'Views/Components/Steps/Steps';
import { RequirementIndexProvider } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';

// These are displayed to the user in titles and labels and
// cannot be arbitrary values
export enum WizardMode {
	VIEW = 'view',
	CREATE = 'create',
	EDIT = 'edit',
}

interface IWizardContextProvider<T> {
	baseUrl: string,
	stepLabels: IStepLabel[],
	children: React.ReactNode;
	initialData: T,
	validators: Array<StepValidator<T>>;
	onChangeAndSave: (abortSignals: AbortSignals, data: T) => Promise<Partial<T> | void>;
	mode: WizardMode;
}

export type StepValidator<T> = (data: T, targetStep: number) =>
	Promise<[boolean, Record<string, string | boolean | undefined | Array<string | boolean | undefined>>]>;

export interface IWizardContext<T> {
	autoSaveStatus: SAVE_STATUS;
	setAutoSaveStatus: Dispatch<SetStateAction<SAVE_STATUS>>
	step: number;
	stepLabels: IStepLabel[];
	nextStep: (newQueryParams?: Record<string, string>) => void;
	previousStep: (newQueryParams?: Record<string, string>) => void;
	goToStep: (step: number, newQueryParams?: Record<string, string>) => void;
	data: T | undefined;
	setData: React.Dispatch<React.SetStateAction<T>>;
	setAndSaveData: (newData: T) => Promise<boolean>,
	validators: Array<StepValidator<T>>;
	errors: Record<string, string>;
	clearErrors: () => void;
	mode: WizardMode;
}

const defaultWizardContext: IWizardContext<any> = {
	step: 0,
	stepLabels: [],
	nextStep: () => {},
	previousStep: () => {},
	goToStep: () => {},
	autoSaveStatus: SAVE_STATUS.UNSAVED,
	setAutoSaveStatus: () => {},
	data: undefined,
	setAndSaveData: () => Promise.resolve(false),
	setData: () => {},
	validators: [],
	errors: {},
	clearErrors: () => {},
	mode: WizardMode.VIEW,
};

export const WizardContext = createContext<IWizardContext<any>>(defaultWizardContext);

/**
 * Run provided validators against wizard data
 */
const runValidators = <T, >(
	validators: StepValidator<T>[],
	data: T,
	targetStep: number,
) => {
	const pendingValidators = validators
		.map(validator => validator(data, targetStep));

	return Promise.all(pendingValidators);
};

/**
 * * Run provided validators and determine earliest invalid step
 * @param validators array of validators to run to determine step validity
 * @param data generic data passed through to validators
 * @param targetStep step attempting to be navigated to
 * @return One-indexed earliest invalid step
 */
const getEarliestInvalidStep = async <T, >(
	validators: StepValidator<T>[],
	data: T,
	targetStep: number,
) => {
	const validationResults = await runValidators(validators, data, targetStep)
		.then(validatorResults => (
			validatorResults.map(result => result[0])
		));

	const earliestInvalidStep = validationResults
		.findIndex(result => result === false);

	return earliestInvalidStep + 1;
};

/**
 * Take provided contextual configuration and provision
 * context consumable via useWizardContext
 *
 * Wizard Component Architecture when fully rendered:
 * <CustomContextWrapper>
 *   <WizardContextProvider> (generic)
 *     <CustomWizard>
 *       <Wizard> (generic)
 *         <CustomCurrentPage/>
 *       </Wizard>
 *     </CustomWizard>
 *   </WizardContextProvider>
 * </CustomContextWrapper>
 *
 * At topmost level React-router <Route> renders
 * <CustomContextWrapper> and provides <CustomWizard> as child.
 * The path of the Route should correspond to the baseUrl prop of
 * the CustomContextWrapper and provide a 'step' path parameter.
 *
 * Custom pages are contextual to wizard.
 *
 * Generic components used to provision Context and standardize
 * layout.
 *
 * CustomCurrentPage contains Wizard.Main containing any
 * combination of .Header, .Sidebar or .Body.
 *
 * High degree of Element variability and data retrieval within
 * wizard pages necessitates alternating Custom-Generic
 * component structure
 */
export function WizardContextProvider<T>({
	baseUrl,
	stepLabels,
	children,
	initialData,
	validators,
	onChangeAndSave,
	mode,
}: IWizardContextProvider<T>) {
	const history = useHistory();
	const queryParams = useQueryParams();

	/**
	 * Retrieve and parse step from url params
	 * NOTE: Step number is always ONE-INDEXED.
	 * Many instances of configuration that corresponds to steps
	 * is zero-indexed
	 */
	const { step: stepParam } = useParams<{ step: string }>();
	const step = toInteger(stepParam);

	const [autoSaveStatus, setAutoSaveStatus] = useState(defaultWizardContext.autoSaveStatus);

	/**
	 * Generic data container for wizard context providers and
	 * steps to store data accessible across wizard
	 */
	const [data, setData] = useState<T>(initialData);
	/**
	 * Stores errors to be displayed in wizard steps
	 */
	const [errors, setErrors] = useState<any>({});

	/**
	 * Attempts to navigate browser to desired step.
	 * If an invalid step is found prior to desired step,
	 * it is navigated to instead.
	 */
	const goToStep = useCallback(async (newStep: number = 1, newQueryParams?: Record<string, string>) => {
		if (autoSaveStatus === SAVE_STATUS.SAVING) {
			return;
		}
		if (newStep > stepLabels.length) {
			goToStep(stepLabels.length, newQueryParams);
			return;
		}

		let targetStep = newStep;
		const earliestInvalidStep = await getEarliestInvalidStep(validators, data, newStep);

		// if there is an invalid step and the invalid step is
		// EARLIER than our desired new step
		if (earliestInvalidStep && earliestInvalidStep < newStep) {
			// override to the earliest step requiring completion
			targetStep = earliestInvalidStep;
		}

		// if valid target step was found to be current step, do nothing
		if (targetStep === step) {
			return;
		}

		const updatedQueryParams = new URLSearchParams(queryParams);
		// eslint-disable-next-line guard-for-in
		for (const paramKey in newQueryParams) {
			updatedQueryParams.set(paramKey, newQueryParams[paramKey]);
		}

		history.replace({
			pathname: `${baseUrl}/${targetStep}`,
			search: `?${updatedQueryParams.toString()}`,
		});
	}, [autoSaveStatus, baseUrl, data, history, queryParams, step, stepLabels.length, validators]);

	/**
	 * Run validators on prior and current step,
	 * and set any resulting errors in wizard state.
	 * This is used when a given step attempts to go 'next',
	 * since we need to ensure the wizard navigates a single
	 * step ahead, at most.
	 */
	const validatePriorStepsAndSetErrors = useCallback(async () => {
		const priorValidators = validators.slice(0, step);
		const validationResults = await runValidators(priorValidators, data, step);

		let valid = true;
		const newErrors = validationResults
			.reduce((accErrors, result) => {
				// if both previous and current validation has passed
				valid = valid && result[0];

				return {
					...accErrors,
					...result[1],
				};
			}, {});

		setErrors(() => ({ ...newErrors }));

		return valid;
	}, [data, step, validators]);

	const [previousStep, nextStep] = useMemo(() => {
		return [
			async (newQueryParams?: Record<string, string>) => goToStep(step - 1, newQueryParams),
			async (newQueryParams?: Record<string, string>) => {
				const validationResult = await validatePriorStepsAndSetErrors();

				if (autoSaveStatus !== SAVE_STATUS.SAVING && validationResult) {
					goToStep(step + 1, newQueryParams);
				}
			},
		];
	}, [goToStep, autoSaveStatus, step, validatePriorStepsAndSetErrors]);

	const onAfterSave = useCallback((savedData: Partial<T>) => {
		setAutoSaveStatus(SAVE_STATUS.SAVED);

		setData(prevState => ({
			...prevState,
			...savedData,
		}));
	}, []);

	const debouncedChangeAndSave = useDebounce(
		(abortSignals, saveData: T) => {
			setAutoSaveStatus(SAVE_STATUS.SAVING);
			return onChangeAndSave(abortSignals, saveData);
		},
		[onChangeAndSave],
		500,
		onAfterSave,
	);

	const setAndSaveData = useCallback(async (newData: T) => {
		setData(prevState => ({
			...prevState,
			...newData,
		}));

		const valid = await validatePriorStepsAndSetErrors();

		if (valid) {
			debouncedChangeAndSave(newData);
		}

		return valid;
	}, [debouncedChangeAndSave, validatePriorStepsAndSetErrors]);

	// automatically rediect based on step conditions
	useEffect(() => {
		if (step === null) {
			goToStep(1);
		} else {
			try {
				// if step is invalid or outside of lower bound
				// go to first step
				if (Number.isNaN(step) || step <= 0) {
					goToStep(1);
					return;
				}

				goToStep(step);
			} catch {
				goToStep(1);
			}
		}
	}, [data, goToStep, history, queryParams, step, validators]);

	const contextValue = useMemo<IWizardContext<T>>(
		() => {
			const clearErrors = () => setErrors({});

			return {
				autoSaveStatus,
				setAutoSaveStatus,
				step,
				stepLabels,
				previousStep,
				nextStep,
				goToStep,
				data,
				setData,
				setAndSaveData,
				validators,
				errors,
				clearErrors,
				mode,
			};
		},
		[
			autoSaveStatus, step, stepLabels, previousStep,
			nextStep, goToStep, data, setAndSaveData,
			validators, errors, mode,
		],
	);

	return (
		<WizardContext.Provider value={contextValue}>
			<RequirementIndexProvider>
				{children}
			</RequirementIndexProvider>
		</WizardContext.Provider>
	);
}

export const useWizardContext = <T, >():IWizardContext<T> => {
	const {
		data: genericData,
		setData: genericSetData,
		...params
	} = useContext(WizardContext);

	const data = genericData as IWizardContext<T>['data'];

	const setData = genericSetData as IWizardContext<T>['setData'];

	return {
		...params,
		data,
		setData,
	};
};
